/**
 * AI Assistant Sidebar Component
 *
 * Main sidebar container for the AI Assistant.
 * Includes header with controls, chat history, and input.
 */

import { type Component, For, Show } from 'solid-js';
import { useAssistant } from './AssistantProvider';
import { ChatHistory } from './ChatHistory';
import { ChatInput } from './ChatInput';

export const AssistantSidebar: Component = () => {
  const { state, close, newConversation, conversations, loadConversation } = useAssistant();

  return (
    <Show when={state().isOpen}>
      {/* Backdrop for mobile */}
      <div class="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={close} />

      {/* Sidebar - fixed position, 400px width to match the content margin */}
      <aside class="fixed right-0 top-0 bottom-0 w-[400px] bg-gray-800 border-l border-gray-700 z-50 flex flex-col shadow-xl max-lg:w-full max-lg:max-w-md">
        {/* Header */}
        <header class="flex items-center justify-between px-4 py-3 border-b border-gray-700 bg-gray-800/90">
          <div class="flex items-center gap-3">
            <div class="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center">
              <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                />
              </svg>
            </div>
            <h2 class="text-lg font-semibold text-white">AI Assistant</h2>
          </div>

          <div class="flex items-center gap-2">
            {/* New conversation button */}
            <button
              onClick={newConversation}
              class="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              title="New conversation"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4v16m8-8H4"
                />
              </svg>
            </button>

            {/* Conversation history dropdown - simplified version */}
            <Show when={conversations().length > 0}>
              <div class="relative group">
                <button class="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors">
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                    />
                  </svg>
                </button>
                <div class="absolute right-0 top-full mt-1 w-64 bg-gray-700 rounded-lg shadow-xl hidden group-hover:block z-50 max-h-80 overflow-y-auto">
                  <For each={conversations().slice(0, 10)}>
                    {(conv) => (
                      <button
                        onClick={() => loadConversation(conv.id)}
                        class={`w-full text-left px-4 py-3 hover:bg-gray-600 transition-colors border-b border-gray-600 last:border-0 ${
                          state().conversationId === conv.id ? 'bg-gray-600' : ''
                        }`}
                      >
                        <div class="text-white text-sm font-medium truncate">{conv.title}</div>
                        <div class="text-gray-400 text-xs truncate">{conv.lastMessage}</div>
                      </button>
                    )}
                  </For>
                </div>
              </div>
            </Show>

            {/* Close button */}
            <button
              onClick={close}
              class="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              title="Close"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </header>

        {/* Context indicator */}
        <Show when={state().currentContext.projectId || state().currentContext.agentId}>
          <div class="px-4 py-2 bg-purple-900/20 border-b border-gray-700 text-sm">
            <span class="text-gray-400">Context: </span>
            <span class="text-purple-400">
              {state().currentContext.projectId
                ? `Project ${state().currentContext.projectId.slice(0, 8)}`
                : state().currentContext.agentId
                  ? `Agent ${state().currentContext.agentId.slice(0, 8)}`
                  : state().currentContext.page}
            </span>
          </div>
        </Show>

        {/* Chat history */}
        <ChatHistory />

        {/* Input */}
        <ChatInput placeholder="Ask me anything..." />
      </aside>
    </Show>
  );
};
